import { useEffect, useRef } from 'react';

import { useAsyncDispatcher } from './useAsyncDispatcher';

const MAX_SET_TIMEOUT_VALUE = 2147483647;

export const useTimeExpiration = (
    milisecondsToExpire: number | null,
    onExpireCallback: () => void,
    blockExpirationHandling?: boolean,
) => {
    const expirationTimeoutHandle = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        let ms = milisecondsToExpire;
        // maximum allowed for setTimeout
        if (ms && ms > MAX_SET_TIMEOUT_VALUE) {
            ms = 1728000000; // just set to 20 days, otherwsie it will resolve immidiately resulting in endless loop
        }

        if (!!ms && ms >= 0 && !blockExpirationHandling) {
            expirationTimeoutHandle.current = setTimeout(() => {
                onExpireCallback();
            }, ms);
        }

        return () => {
            if (expirationTimeoutHandle.current) {
                clearTimeout(expirationTimeoutHandle.current);
            }
        };
    }, [milisecondsToExpire, blockExpirationHandling, onExpireCallback]);
};

export const useTimeExpirationWithAsyncEnsure = (
    milisecondsToExpire: number | null,
    asyncEnsureCall: (...args: unknown[]) => Promise<unknown>,
    onExpireCallback: () => void,
    blockExpirationHandling?: boolean,
) => {
    const {
        execute: executeEnsure,
        status: fetchStatus,
        reset,
    } = useAsyncDispatcher(asyncEnsureCall);

    useTimeExpiration(milisecondsToExpire, executeEnsure, blockExpirationHandling);

    useEffect(() => {
        if (fetchStatus === 'success') {
            if (!milisecondsToExpire || milisecondsToExpire < 0) {
                reset();
                onExpireCallback();
            }
        }
    }, [milisecondsToExpire, fetchStatus, onExpireCallback, reset]);
};

export const useTimeExpirationIntervalReport = (
    expirationTime: Date,
    interval: number,
    reportFunction: (timeDifferenceMiliseconds?: number) => void,
) => {
    const intervalHandle = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (expirationTime) {
            intervalHandle.current = setInterval(() => {
                const timeDifferenceMiliseconds =
                    new Date(expirationTime).getTime() - new Date().getTime();
                if (timeDifferenceMiliseconds > 0) reportFunction(timeDifferenceMiliseconds);
            }, interval);
        }

        return () => {
            if (intervalHandle.current) {
                clearTimeout(intervalHandle.current);
            }
        };
    }, [expirationTime, reportFunction, interval]);
};
